import { Show } from "@refinedev/antd";
import { useCan, useNotification, useOne } from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Checkbox, Descriptions, Input, Space, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { api } from "../api";
import { DATE_TIME_FORMAT, REPORT_REASONS, REPORT_STATUS } from "../labels";
import type { MessageReport, ReportedMessage } from "../types";
import { PARTY } from "./message-reports";

/**
 * /message-reports/:id (permission `messages.moderate`): the report, the conversation's
 * messages on request (written to the audit log), and the decision: a note, and optionally
 * closing the conversation so no one can write in it.
 */
export function MessageReportShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const queryClient = useQueryClient();
  const { result: report, query } = useOne<MessageReport>({ resource: "message-reports", id });
  const { data: canSeeStudents } = useCan({ resource: "students", action: "list" });
  const { data: canSeeTutors } = useCan({ resource: "tutors", action: "list" });
  const [messages, setMessages] = useState<ReportedMessage[] | null>(null);
  const [resolution, setResolution] = useState("");
  const [closeConversation, setCloseConversation] = useState(false);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  const reveal = () =>
    run(async () => {
      const answer = await api<{ messages: ReportedMessage[] }>(
        `/api/office/message-reports/${encodeURIComponent(id)}/conversation`,
        { method: "POST" },
      );
      setMessages(answer.messages);
    });

  const resolve = () =>
    run(async () => {
      await api(`/api/office/message-reports/${encodeURIComponent(id)}/resolve`, {
        method: "POST",
        body: { resolution: resolution.trim(), closeConversation },
      });
      open?.({ type: "success", message: "Bildirim sonuçlandırıldı." });
      await Promise.all([
        query.refetch(),
        queryClient.invalidateQueries({ queryKey: ["office", "badges"] }),
      ]);
    });

  return (
    <Show title="Sohbet bildirimi" isLoading={query.isLoading} canEdit={false} canDelete={false}>
      {report ? (
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Öğrenci">
              {canSeeStudents?.can ? (
                <Link to={`/students/${report.student.id}`}>{report.student.name}</Link>
              ) : (
                report.student.name
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Eğitmen">
              {canSeeTutors?.can ? (
                <Link to={`/tutors/${report.tutor.id}`}>{report.tutor.name}</Link>
              ) : (
                report.tutor.name
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Bildiren">{PARTY[report.reporter]}</Descriptions.Item>
            <Descriptions.Item label="Neden">
              {REPORT_REASONS[report.reason]}
              {report.note ? ` · ${report.note}` : ""}
            </Descriptions.Item>
            <Descriptions.Item label="Tarih">
              {dayjs(report.createdAt).format(DATE_TIME_FORMAT)}
            </Descriptions.Item>
            <Descriptions.Item label="Durum">
              <Tag color={REPORT_STATUS[report.status]?.color}>
                {REPORT_STATUS[report.status]?.label}
              </Tag>
              {report.closedAt ? <Tag color="red">Sohbet kapalı</Tag> : null}
              {report.resolution
                ? ` ${report.resolution} (${report.resolvedBy ?? "—"}, ${dayjs(report.resolvedAt).format(DATE_TIME_FORMAT)})`
                : ""}
            </Descriptions.Item>
          </Descriptions>

          <Card size="small" title="Mesajlar">
            {messages ? (
              <div
                role="log"
                aria-label="Mesajlar"
                style={{
                  maxHeight: "55vh",
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                {messages.length === 0 ? (
                  <Typography.Text type="secondary">Mesaj yok.</Typography.Text>
                ) : null}
                {messages.map((message) => (
                  <div key={message.id}>
                    <Typography.Text strong>
                      {message.sender === "STUDENT" ? report.student.name : report.tutor.name}
                    </Typography.Text>{" "}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(message.createdAt).format(DATE_TIME_FORMAT)}
                    </Typography.Text>
                    <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                      {message.body}
                    </Typography.Paragraph>
                  </div>
                ))}
              </div>
            ) : (
              <Space direction="vertical">
                <Typography.Paragraph type="secondary">
                  Mesajlar kişiseldir; açılması Kayıtlar&apos;a yazılır.
                </Typography.Paragraph>
                <Button onClick={reveal} loading={busy}>
                  Sohbeti göster
                </Button>
              </Space>
            )}
          </Card>

          {report.status === "OPEN" ? (
            <Card size="small" title="Sonuçlandır">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.TextArea
                  aria-label="Sonuç"
                  placeholder="Ne yapıldı? (ör. eğitmen uyarıldı)"
                  maxLength={500}
                  autoSize={{ minRows: 2 }}
                  value={resolution}
                  onChange={(event) => setResolution(event.target.value)}
                />
                <Checkbox
                  checked={closeConversation}
                  onChange={(event) => setCloseConversation(event.target.checked)}
                >
                  Sohbeti kapat (kimse yeni mesaj yazamaz)
                </Checkbox>
                <Button
                  type="primary"
                  onClick={resolve}
                  loading={busy}
                  disabled={!resolution.trim()}
                >
                  Sonuçlandır
                </Button>
              </Space>
            </Card>
          ) : (
            <Alert type="success" showIcon message="Bu bildirim sonuçlandırıldı." />
          )}
        </Space>
      ) : null}
    </Show>
  );
}
